import moment from "moment/moment";
import React, { useEffect, useMemo, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import { FiFileText, FiRefreshCw, FiSearch, FiSend } from "react-icons/fi";
import { ToastContainer, toast } from "react-toastify";
import { useDebounce } from "use-debounce";
import Layout from "../../../components/layouts/Layout";
import {
  Table,
  TableBody,
  TableHeader,
  TablePagination,
} from "../../../components/table";
import {
  createAutomationFlowFromTemplate,
  fetchLiveEvents,
  fetchAutomationTemplates,
  SMS_AUTOMATION_TEMPLATE_ENDPOINT,
} from "./automationTemplateApi";
import SmsAccessGuard from "../Components/SmsAccessGuard";
import TemplatePreviewModal from "./TemplatePreviewModal";
import LocalStorage from "../../../hook/LocalStorage";
import { DEFAULT_INITIAL_MESSAGE_SEND_TIME } from "./automationConstants";

const readFirstDefined = (source, keys) => {
  if (!source) {
    return null;
  }

  const key = keys.find((item) => source[item] !== undefined && source[item] !== null);

  return key ? source[key] : null;
};

const normaliseTemplate = (template) => ({
  ...template,
  id: readFirstDefined(template, ["id", "template_id", "templateId", "uuid"]),
  name: readFirstDefined(template, ["name", "template_name", "templateName", "title"]),
  category: readFirstDefined(template, ["category", "category_name", "categoryName", "type"]),
  goal: readFirstDefined(template, ["goal", "goal_name", "goalName", "objective"]),
  description: readFirstDefined(template, ["description", "template_description", "summary"]),
  status: readFirstDefined(template, ["status", "is_active", "active", "enabled"]),
  updatedAt: readFirstDefined(template, ["updated_at", "updatedAt", "last_updated", "lastUpdated"]),
});

const normaliseEvent = (event) => ({
  ...event,
  id: readFirstDefined(event, ["id", "event_id", "eventId", "uuid"]),
  name: readFirstDefined(event, ["event_name", "eventName", "name", "title"]),
});

const normaliseEventsResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.event ||
    source.events ||
    source.live_events ||
    source.liveEvents ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = Array.isArray(collection) ? collection : collection.data || [];

  return items.map(normaliseEvent);
};

const normaliseTemplatesResponse = (payload) => {
  const source = payload?.data || payload || {};
  const collection =
    source.templates ||
    source.automation_templates ||
    source.automationTemplates ||
    source.data ||
    (Array.isArray(source) ? source : []);
  const items = Array.isArray(collection) ? collection : collection.data || [];

  return {
    data: items.map(normaliseTemplate),
    currentPage: collection.current_page || source.current_page || 1,
    total: collection.total || source.total || items.length,
    perPage: collection.per_page || source.per_page || 10,
    from: collection.from || source.from || (items.length ? 1 : 0),
    to: collection.to || source.to || items.length,
  };
};

const normaliseCreatedFlow = (payload) => {
  const source = payload?.data || payload || {};
  const flow = source.flow || source.automation_flow || source.automationFlow || source.data || source;

  return {
    id: readFirstDefined(flow, ["id", "flow_id", "flowId", "automation_flow_id", "automationFlowId"]),
  };
};

const formatStatus = (status) => {
  if (status === null || status === undefined || status === "") {
    return "Not applicable";
  }

  if (status === true || status === 1 || status === "1" || status === "active") {
    return "Active";
  }

  if (status === false || status === 0 || status === "0" || status === "inactive") {
    return "Inactive";
  }

  return String(status);
};

const formatDate = (date) => {
  if (!date) {
    return "Not available";
  }

  const parsedDate = moment(date);

  return parsedDate.isValid() ? parsedDate.format("MMM DD, YYYY") : "Not available";
};

const AutomationTemplates = () => {
  const history = useHistory();
  const { user } = LocalStorage();
  const [templates, setTemplates] = useState({
    data: [],
    currentPage: 1,
    total: 0,
    perPage: 10,
    from: 0,
    to: 0,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchKeyWord, setSearchKeyWord] = useState("");
  const [searchKeyWordDebounce] = useDebounce(searchKeyWord, 600);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [goalFilter, setGoalFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [creatingTemplateId, setCreatingTemplateId] = useState(null);
  const [events, setEvents] = useState([]);
  const [selectedEventByTemplate, setSelectedEventByTemplate] = useState({});
  const [eventValidationByTemplate, setEventValidationByTemplate] = useState({});

  const hasTemplates = useMemo(() => templates.data && templates.data.length > 0, [templates.data]);
  const categoryOptions = useMemo(
    () => [...new Set(templates.data.map((template) => template.category).filter(Boolean))],
    [templates.data]
  );
  const goalOptions = useMemo(
    () => [...new Set(templates.data.map((template) => template.goal).filter(Boolean))],
    [templates.data]
  );

  const handleSearch = (query) => {
    setSearchKeyWord(query);
    setCurrentPage(1);
  };

  useEffect(() => {
    let isMounted = true;

    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const response = await fetchAutomationTemplates({
          page: currentPage,
          search: searchKeyWordDebounce,
          category: categoryFilter || undefined,
          goal: goalFilter || undefined,
          status: statusFilter || undefined,
        });

        if (isMounted) {
          setTemplates(normaliseTemplatesResponse(response.data));
        }
      } catch (error) {
        if (isMounted) {
          setTemplates({
            data: [],
            currentPage: 1,
            total: 0,
            perPage: 10,
            from: 0,
            to: 0,
          });
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchTemplates();

    return () => {
      isMounted = false;
    };
  }, [categoryFilter, currentPage, goalFilter, searchKeyWordDebounce, statusFilter]);

  useEffect(() => {
    let isMounted = true;

    const fetchEvents = async () => {
      try {
        const eventsResponse = await fetchLiveEvents({
          per_page: 1000,
          page: 1,
          user_id: user?.id,
        });

        if (isMounted) {
          setEvents(normaliseEventsResponse(eventsResponse.data));
        }
      } catch (error) {
        if (isMounted) {
          setEvents([]);
        }
      }
    };

    fetchEvents();

    return () => {
      isMounted = false;
    };
  }, [user?.id]);

  const handleEventChange = (templateId, eventId) => {
    setSelectedEventByTemplate((current) => ({
      ...current,
      [templateId]: eventId,
    }));
    setEventValidationByTemplate((current) => ({
      ...current,
      [templateId]: "",
    }));
  };

  const handleFilterChange = (setter) => (event) => {
    setter(event.target.value);
    setCurrentPage(1);
  };

  const clearFilters = () => {
    setSearchKeyWord("");
    setCategoryFilter("");
    setGoalFilter("");
    setStatusFilter("");
    setCurrentPage(1);
  };

  const handleUseTemplate = async (template) => {
    const eventId = selectedEventByTemplate[template.id];

    if (!eventId) {
      setEventValidationByTemplate((current) => ({
        ...current,
        [template.id]: "Event is required.",
      }));
      return;
    }

    try {
      setCreatingTemplateId(template.id);
      const response = await createAutomationFlowFromTemplate(template.id, {
        event_id: eventId,
        initial_message_send_time: DEFAULT_INITIAL_MESSAGE_SEND_TIME,
      });
      const flow = normaliseCreatedFlow(response.data);

      toast.success("Automation flow created from template.");

      setTimeout(() => {
        if (flow.id) {
          history.push(`/conversions/automation-flows/${flow.id}`);
        } else {
          history.push("/conversions/automation-flows/new");
        }
      }, 600);
    } catch (error) {
      const responseData = error?.response?.data || {};
      const eventError = responseData?.errors?.event_id;
      const message = Array.isArray(eventError)
        ? eventError[0]
        : eventError || responseData.message || "Unable to create automation flow.";

      setEventValidationByTemplate((current) => ({
        ...current,
        [template.id]: message,
      }));
      setCreatingTemplateId(null);
    }
  };

  const tabs = [
    ["Overview", "/conversions"],
    ["Conversations", "/conversions/conversations"],
    ["Notifications", "/conversions/notifications"],
    ["Analytics", "/conversions/analytics"],
    ["My Automation Flows", "/conversions/automation-flows"],
    ["Browse Templates", "/conversions/sms-automation-templates", true],
    ["Billing", "/conversions/billing"],
  ];

  const filtersApplied = Boolean(searchKeyWord || categoryFilter || goalFilter || statusFilter);

  return (
    <SmsAccessGuard>
      <Layout>
        <main className="page automation-templates-dashboard-page">
          <section className="automation-templates-hero">
            <div>
              <h1>SMS Automation Templates</h1>
              <p>Browse ready-made SMS automation templates and launch them for your events.</p>
            </div>
            <div className="automation-templates-hero-art" aria-hidden="true">
              <span className="automation-template-spark spark-one">✦</span>
              <span className="automation-template-spark spark-two">✦</span>
              <span className="automation-template-message">•••</span>
              <span className="automation-template-phone">
                <i />
                <b /><b /><b />
                <em /><em />
              </span>
              <FiSend className="automation-template-send" />
              <span className="automation-template-dots" />
            </div>
          </section>

          <nav className="conversations-tabs automation-templates-tabs" aria-label="Conversations sections">
            {tabs.map(([label, path, active]) => <Link key={path} to={path} className={active ? "active" : ""}>{label}</Link>)}
          </nav>

          <section className="automation-template-filter-card">
            <div className="automation-template-filters">
              <label className="automation-template-search">
                <FiSearch />
                <input value={searchKeyWord} onChange={(event) => handleSearch(event.target.value)} placeholder="Search templates by name or keyword" aria-label="Search automation templates" />
              </label>
              <label><span>Category</span><select value={categoryFilter} onChange={handleFilterChange(setCategoryFilter)}><option value="">All Categories</option>{categoryOptions.map((category) => <option key={category} value={category}>{category}</option>)}</select></label>
              <label><span>Goal</span><select value={goalFilter} onChange={handleFilterChange(setGoalFilter)}><option value="">All Goals</option>{goalOptions.map((goal) => <option key={goal} value={goal}>{goal}</option>)}</select></label>
              <label><span>Status</span><select value={statusFilter} onChange={handleFilterChange(setStatusFilter)}><option value="">All Statuses</option><option value="active">Active</option><option value="inactive">Inactive</option></select></label>
              <button type="button" onClick={clearFilters} disabled={!filtersApplied}><FiRefreshCw /> Clear Filters</button>
            </div>
          </section>

          <section className="automation-template-table-card">
            <div className="automation-template-table-wrap">
              <Table>
                <TableHeader><tr><th>Template Name</th><th>Category</th><th>Description</th><th>Status</th><th>Last Updated</th><th>Event</th><th>Actions</th></tr></TableHeader>
                <TableBody>
                  {loading ? <tr><td colSpan={7}><div className="automation-template-table-state"><span className="spinner-border" role="status" aria-label="Loading templates" /></div></td></tr> : null}
                  {!loading && hasTemplates ? templates.data.map((template) => {
                    const status = formatStatus(template.status);
                    return (
                      <tr key={template.id}>
                        <td><div className="automation-template-name"><span><FiFileText /></span><strong>{template.name || "Untitled Template"}</strong></div></td>
                        <td><span className="template-category-chip">{template.category || "Uncategorized"}</span></td>
                        <td className="automation-template-description">{template.description || "No description available."}</td>
                        <td><span className={`template-status-chip status-${status.toLowerCase().replace(/\s+/g, "-")}`}><i />{status}</span></td>
                        <td>{formatDate(template.updatedAt)}</td>
                        <td>
                          <select aria-label={`Event for ${template.name || "automation template"}`} className={`template-event-select ${eventValidationByTemplate[template.id] ? "is-invalid" : ""}`} value={selectedEventByTemplate[template.id] || ""} onChange={(event) => handleEventChange(template.id, event.target.value)}>
                            <option value="">Select event</option>
                            {events.map((event) => <option key={event.id} value={event.id}>{event.name || `Event #${event.id}`}</option>)}
                          </select>
                          {eventValidationByTemplate[template.id] ? <div className="template-event-error">{eventValidationByTemplate[template.id]}</div> : null}
                        </td>
                        <td><div className="template-row-actions">
                          <button type="button" className="template-preview-button" data-toggle="modal" data-target="#automation-template-preview" onClick={() => setSelectedTemplate(template)}>Preview</button>
                          <button type="button" className="template-use-button" disabled={creatingTemplateId === template.id} onClick={() => handleUseTemplate(template)}>{creatingTemplateId === template.id ? "Creating..." : "Use Template"}</button>
                        </div></td>
                      </tr>
                    );
                  }) : null}
                  {!loading && !hasTemplates ? <tr><td colSpan={7}><div className="automation-template-table-state empty">No automation templates available.</div></td></tr> : null}
                </TableBody>
              </Table>
            </div>

            {!loading && templates.total > 0 ? <div className="automation-template-pagination"><TablePagination currentPage={templates.currentPage} total={templates.total} perPage={templates.perPage} from={templates.from} to={templates.to} onChange={(page) => setCurrentPage(page)} /></div> : null}
          </section>

          <TemplatePreviewModal template={selectedTemplate} />
          <ToastContainer position="bottom-right" autoClose={2500} />
        </main>
      </Layout>
    </SmsAccessGuard>
  );
};

export { SMS_AUTOMATION_TEMPLATE_ENDPOINT };
export default AutomationTemplates;
